import { postCommunication, communicationError } from '../../lib/communicationRequests';
import React, { useMemo, useCallback, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import CircularProgress from '@mui/joy/CircularProgress';
import type { InboxItem } from '../../types/inbox';
import UnifiedInboxItem from './UnifiedInboxItem';
import { filterInboxItems } from './inboxItemFilters';
import { getSmsPhoneNumber, getSmsLocalNumber } from './inboxItemUtils';
import api from '../../lib/api';

interface UnifiedInboxListProps {
  items: InboxItem[];
  loading: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  selectedItems: Set<number>;
  expandedItems: Set<number>;
  contentFilter: 'all' | 'email' | 'call' | 'sms' | 'task';
  assigneeFilter: string | null;
  sortOrder: 'oldest' | 'newest' | 'priority';
  onItemSelect: (itemId: number, selected: boolean) => void;
  onItemExpand: (itemId: number) => void;
  onItemDone: (itemId: number) => void;
  onItemSnooze: (itemId: number) => void;
  onItemEmail: (item: InboxItem) => void;
  onItemCall: (item: InboxItem) => void;
  onItemText: (item: InboxItem) => void;
  onItemReply: (itemId: number, content: string, type: 'email' | 'sms') => void;
  onRefresh: () => void;
  onLoadMore?: () => void;
}

const UnifiedInboxList: React.FC<UnifiedInboxListProps> = ({
  items,
  loading,
  loadingMore = false,
  hasMore = false,
  selectedItems,
  expandedItems,
  contentFilter,
  assigneeFilter,
  sortOrder,
  onItemSelect,
  onItemExpand,
  onItemDone,
  onItemSnooze,
  onItemEmail,
  onItemCall,
  onItemText,
  onItemReply,
  onRefresh,
  onLoadMore,
}) => {
  const parentRef = React.useRef<HTMLDivElement>(null);
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  // Filter items based on content filter
  const filteredItems = useMemo(() => {
    const filtered = [...filterInboxItems(items, contentFilter, assigneeFilter)];

    // Apply sort order
    filtered.sort((a, b) => {
      switch (sortOrder) {
        case 'oldest':
          return new Date(a.last_event_at).getTime() - new Date(b.last_event_at).getTime();
        case 'newest':
          return new Date(b.last_event_at).getTime() - new Date(a.last_event_at).getTime();
        case 'priority':
          // Sort by priority (urgent > high > normal > low), then by date
          const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
          const aPriority = priorityOrder[a.priority] || 0;
          const bPriority = priorityOrder[b.priority] || 0;
          if (aPriority !== bPriority) {
            return bPriority - aPriority;
          }
          return new Date(b.last_event_at).getTime() - new Date(a.last_event_at).getTime();
        default:
          return 0;
      }
    });

    return filtered;
  }, [items, contentFilter, assigneeFilter, sortOrder]);

  // The list renders every row in normal document flow rather than
  // absolutely-positioned virtual rows. Rows here vary hugely in height and
  // grow after mount as their content loads (an SMS thread, a call recording),
  // which a virtualizer has to guess at and repeatedly got wrong — expanded
  // rows ended up overlapping the rows below. The feed is paginated to ~20
  // rows a page, so plain rendering is cheap and expand/collapse behaves like
  // an ordinary accordion.

  // Handle item actions
  const handleItemDone = useCallback(async (itemId: number) => {
    try {
      await api.patch(`/api/inbox/${itemId}/complete/`, {});
      onItemDone(itemId);
    } catch (error) {
      console.error('Failed to mark item as done:', error);
    }
  }, [onItemDone]);

  const handleItemSnooze = useCallback((itemId: number) => {
    onItemSnooze(itemId);
  }, [onItemSnooze]);

  const handleItemReply = useCallback(async (itemId: number, content: string, type: 'email' | 'sms') => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    try {
      if (type === 'email') {
        // Extract thread ID from source_id (format: "thread-{id}")
        const threadId = item.source_id.replace('thread-', '');
        
        await api.post(`/api/inbox/email/${threadId}/reply/`, {
          body_text: content,
          body_html: `<p>${content.replace(/\n/g, '<br>')}</p>`,
        });
      } else if (type === 'sms') {
        const phoneNumber = getSmsPhoneNumber(item);
        if (!phoneNumber) {
          console.error('Cannot send SMS: no phone number on inbox item', item.id);
          return;
        }
        await postCommunication('/api/inbox/sms/send/', {
          to_number: phoneNumber,
          from_number: getSmsLocalNumber(item),
          body: content,
          purpose: 'conversation',
          ...(item.lead ? { lead_id: item.lead } : {}),
        });
      }
      onItemReply(itemId, content, type);
      onRefresh();
    } catch (error) {
      window.alert(communicationError(error));
    }
  }, [items, onItemReply, onRefresh]);

  // Infinite scroll: observe a sentinel at the end of the list.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loadingMore || !onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMore();
      },
      { root: parentRef.current, threshold: 0.1, rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore, filteredItems.length]);

  if (loading && filteredItems.length === 0) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (filteredItems.length === 0) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <Typography>
          {contentFilter !== 'all' || assigneeFilter
            ? 'No items match your filters'
            : 'No items in your inbox'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={parentRef}
      sx={{
        flex: 1,
        overflow: 'auto',
        p: { xs: 1, md: 2 },
      }}
    >
      {filteredItems.map((item) => (
        <UnifiedInboxItem
          key={item.id}
          item={item}
          isSelected={selectedItems.has(item.id)}
          isExpanded={expandedItems.has(item.id)}
          onToggleSelect={(checked) => onItemSelect(item.id, checked)}
          onToggleExpand={() => onItemExpand(item.id)}
          onDone={() => handleItemDone(item.id)}
          onSnooze={() => handleItemSnooze(item.id)}
          onEmail={() => onItemEmail(item)}
          onCall={() => onItemCall(item)}
          onText={() => onItemText(item)}
          onReply={(content, type) => handleItemReply(item.id, content, type)}
        />
      ))}

      {/* Infinite-scroll sentinel: observed rather than watching the last row,
          so expanding a row can't retrigger a fetch. */}
      <Box ref={sentinelRef} sx={{ height: 1 }} />

      {loadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size="sm" />
        </Box>
      )}
    </Box>
  );
};

export default UnifiedInboxList;